import { WalletTypes } from '@constants/enums';
import clientSvc from '@efex-services/client/clientSvc';
import mixpanelSvc from '@efex-services/mixpanel/mixpanelSvc';
import { fetchPayables } from '@pages/payables/store';
import { selectProfileData } from '@pages/profile/store/selectors';
import { aesDecryptCBC } from '@utils/crypt';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@/store';
import { fetchWallets } from '@/modules/wallets/store';
import { selectWalletsData } from '@/modules/wallets/selectors';
import { fetchCombinedBalance } from '@/modules/combined_balance/store';
import PAYMENT_STEPS from '../constants';
import { useFormData } from '../context';
import FormatPaymentService from '../services/formatPayment.service';
import { PaymentMethodsState, TransactionTypes } from '../types';

interface PaymentModalProps {
  onClose: () => void;
  open: boolean;
  selectedClientEmail: string | null;
}

const usePaymentController = ({
  onClose,
  selectedClientEmail,
}: PaymentModalProps) => {
  const userProfile = useSelector(selectProfileData);
  const walletsState = useSelector(selectWalletsData);
  const [selectedClientController, setSelectedClientController] = useState<
    string | undefined
  >();

  const dispatch = useAppDispatch();

  const formatPayment = useMemo(() => new FormatPaymentService(), []);

  const enterpriseId = useMemo(() => {
    if (userProfile?.company) return userProfile?.company?.enterpriseId;
    return null;
  }, [userProfile]);

  const featurePayment = useMemo(
    () => userProfile?.featurePayment ?? false,
    [userProfile]
  );

  const {
    data: formData,
    responseData,
    responseError,
    responseLoading,
    setFormValues,
    generateTransfer,
    generateSwiftTransfer,
    clearFormValues,
  } = useFormData();

  const steps = Array.from(Array(5).keys()).map((item) => item.toString());

  const [formStep, setFormStep] = useState<number>(PAYMENT_STEPS.SELECT_CLIENT);
  const [paymentInProcess, setPaymentInProcess] = useState<boolean>(false);
  const [wooMoment, setWooMoment] = useState<boolean>(false);
  const [loadingPreselectedClient, setLoadingPreselectedClient] =
    useState<boolean>(false);

  const nextFormStep = () => setFormStep((currentStep) => currentStep + 1);

  const prevFormStep = () => setFormStep((currentStep) => currentStep - 1);

  const goToFormStep = useCallback((value: number) => setFormStep(value), []);

  const requestBalances = () => {
    if (enterpriseId) {
      dispatch(fetchWallets(enterpriseId));
      dispatch(fetchCombinedBalance(enterpriseId));
    }
  };

  const closeAndClearContext = () => {
    clearFormValues();
    dispatch(fetchPayables());
    onClose();
  };

  const clearAndNewPayment = () => {
    requestBalances();
    clearFormValues();
    setWooMoment(false);
    goToFormStep(PAYMENT_STEPS.SELECT_CLIENT);
  };

  const createTransfer = async () => {
    setPaymentInProcess(true);
    let newFormData = await formatPayment.build(formData, featurePayment);
    if (formData.isForward && newFormData) {
      const walletBalance = walletsState?.find(
        (wallet) =>
          wallet?.currency.name === newFormData?.desCurrency &&
          wallet?.wallet.wallet_type === WalletTypes.GLOBAL_ACCOUNT
      )?.uuid;
      newFormData = { ...newFormData, walletBalance };
    }
    if (
      newFormData &&
      (newFormData.transactionTypeId === TransactionTypes.swift ||
        newFormData.transactionTypeId === TransactionTypes.promoterSwift ||
        featurePayment ||
        formData.isForward) &&
      enterpriseId
    ) {
      generateSwiftTransfer(newFormData, enterpriseId);
    } else if (newFormData) {
      generateTransfer(newFormData);
    }
  };

  const sendMixpanelEvent = () => {
    if (!userProfile?.isTestUser) {
      const {
        client,
        reverse,
        amount,
        resultAmount,
        isSchedule,
        isPendingTransfer,
        currency,
        clientCurrency,
      } = formData;

      mixpanelSvc.track('Payable', {
        amount: reverse ? resultAmount : amount,
        desAmount: reverse ? amount : resultAmount,
        currency: reverse ? clientCurrency : currency,
        desCurrency: reverse ? currency : clientCurrency,
        userCountry: userProfile?.company.country,
        clientCountry: client?.country,
        isPaymentScheduled: isSchedule,
        isPendingTransfer,
      });
    }
  };

  useEffect(() => {
    if (responseData && !responseError && !responseLoading) {
      if (!formData.dueDate) {
        setFormStep(PAYMENT_STEPS.SUCCESS);
      } else {
        setFormStep(PAYMENT_STEPS.PENDING_OF_TRANSFER);
      }
      if (
        (responseData && responseData.saved_amount) ||
        responseData.savedAmount
      ) {
        setWooMoment(true);
      }
      sendMixpanelEvent();
    }
    setPaymentInProcess(responseLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.dueDate, responseData, responseError, responseLoading]);

  useEffect(() => {
    if (responseError && (responseError as any).response.data.status === 515) {
      setFormStep(PAYMENT_STEPS.NOT_PROCESSED);
      return;
    }
    if (responseError && (responseError as any).response.data.status !== 515) {
      setFormStep(PAYMENT_STEPS.ERROR);
    }
  }, [responseError]);

  useEffect(() => {
    if (
      formData.paymentMethod === 'balance' &&
      formStep === PAYMENT_STEPS.WALLET_PAYMENT
    ) {
      createTransfer();
    }
    if (
      formData.paymentMethod === 'bank' &&
      formStep === PAYMENT_STEPS.WALLET_PAYMENT
    ) {
      nextFormStep();
    }
    if (
      formData.paymentMethod === PaymentMethodsState['checkbox-schedule'] ||
      (formData.paymentMethod === PaymentMethodsState['checkbox-forward'] &&
        formStep === PAYMENT_STEPS.WALLET_PAYMENT)
    ) {
      goToFormStep(PAYMENT_STEPS.SCHEDULE_PAYMENT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, formStep]);

  const getPreselectedClient = useCallback(async () => {
    if (selectedClientController) {
      try {
        setLoadingPreselectedClient(true);
        if (!enterpriseId) return;
        const client = await clientSvc.searchContactByEmail(
          selectedClientController,
          enterpriseId
        );
        const filteredClient = client.clientData.find(
          (innerClient) =>
            aesDecryptCBC(innerClient.email || '') === selectedClientController
        );
        if (filteredClient) {
          setFormValues({
            ...formData,
            client: filteredClient,
          });
          goToFormStep(PAYMENT_STEPS.SELECT_AMOUNT);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingPreselectedClient(false);
        setSelectedClientController(undefined);
      }
    }
  }, [
    selectedClientController,
    formData,
    setFormValues,
    goToFormStep,
    enterpriseId,
  ]);

  useEffect(() => {
    if (selectedClientController) {
      getPreselectedClient();
    }
  }, [getPreselectedClient, selectedClientController]);

  useEffect(() => {
    if (selectedClientEmail) {
      setSelectedClientController(selectedClientEmail);
    }
  }, [selectedClientEmail]);

  return {
    steps,
    paymentInProcess,
    formStep,
    formData,
    wooMoment,
    responseData,
    loadingPreselectedClient,
    prevFormStep,
    nextFormStep,
    createTransfer,
    goToFormStep,
    closeAndClearContext,
    clearAndNewPayment,
  };
};

export default usePaymentController;
